import React, { useState } from "react";
import "./customInput.css";
import { useSharedState } from "../../context/formsContext";
import { ConsoleView } from "react-device-detect";
import { getProperty } from "../../utils/apiResponseHelper";

const CustomInput = ({
  placeholder,
  type = "text",
  inputType,
  value = "",
  onChange,
  onBlur,
  handleChange,
  isTitleDropdown,
  isGenderDropdown,
  isYearDropdown,
  isAddress,
  isVerification,
  addressData,
  errorMessage = "",
  autoComplete = "off",
  name,
  onKeyDown,
  inputRef,
  maxLength,
  getFullAddressLookup,
  isGrammarSchool,
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showGrammarSuggestions, setShowGrammarSuggestions] = useState(false);

  const { setSchoolList, schoolList, setValue, grammarSchools } = useSharedState();

  const handleAddressChange = (event) => {
    onChange(event);
    setShowSuggestions(true);
  };

  const handleSuggestionSelect = (suggestion) => {
    getFullAddressLookup(suggestion.id);
    handleChange(suggestion.id);
    setShowSuggestions(false);
  };

  const handleGrammarChange = (event) => {
    onChange(event);
    // If schoolList is empty but we have grammarSchools, populate it
    if (schoolList?.length === 0 && grammarSchools?.length > 0) {
      const allSchools = Array.isArray(grammarSchools) ? grammarSchools.slice(0, 50) : [];
      setSchoolList(allSchools);
    }
    if (schoolList?.length > 0) {
      setShowGrammarSuggestions(true);
    }
  };

  const handleGrammarSelect = (school) => {
    // Get school name with fallback for case sensitivity
    const schoolName = getProperty(school, 'schoolName') || school.SchoolName || school.schoolName || '';
    setValue((prevValue) => ({
      ...prevValue,
      school: schoolName,
    }));
    setShowGrammarSuggestions(false);
  };

  if (isAddress) {
    return (
      <div className="w-full mb-4 relative">
        <input
          placeholder={placeholder}
          autoComplete={autoComplete}
          type={inputType || "text"}
          name={name}
          className={`p-4 w-full ${
            errorMessage ? "border-red" : "border"
          } focus:outline-none focus:ring-0`}
          value={value}
          onChange={handleAddressChange}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        />
        {errorMessage && (
          <p className="flex justify-start mt-4 text-red-500 text-sm">
            {errorMessage}
          </p>
        )}
        {showSuggestions &&
          addressData &&
          addressData.suggestions &&
          Object.keys(addressData).length > 0 && (
            <ul className="absolute z-10 left-0 w-full border-not-rounded rounded-2xl bg-white">
              {addressData.suggestions.map((suggestion) => (
                <li
                  key={suggestion.id}
                  className="text-lg p-2 hover:bg-blue-700 hover:rounded-xl hover:text-white cursor-pointer"
                  onClick={() => handleSuggestionSelect(suggestion)}
                >
                  {suggestion.address}
                </li>
              ))}
            </ul>
          )}
      </div>
    );
  }

  if (isGrammarSchool) {
    return (
      <div className="w-full mb-4 relative">
        <input
          placeholder={placeholder}
          autoComplete={autoComplete}
          type={inputType || "text"}
          name={name}
          className={`p-4 w-full ${
            errorMessage ? "border-red" : "border"
          } focus:outline-none focus:ring-0`}
          value={value}
          onChange={handleGrammarChange}
          onFocus={() => {
            // If schoolList is empty but we have grammarSchools, populate it
            if (schoolList?.length === 0 && grammarSchools?.length > 0) {
              const allSchools = Array.isArray(grammarSchools) ? grammarSchools.slice(0, 50) : [];
              setSchoolList(allSchools);
            }
            setShowGrammarSuggestions(true);
          }}
          onBlur={() => setTimeout(() => setShowGrammarSuggestions(false), 200)}
        />
        {errorMessage && (
          <p className="flex justify-start mt-4 text-red-500 text-sm">
            {errorMessage}
          </p>
        )}
        {showGrammarSuggestions && schoolList.length > 0 && (
          <ul className="absolute z-10 left-0 w-full border-not-rounded rounded-2xl bg-white shadow-lg max-h-60 overflow-y-auto">
            {schoolList.map((school, index) => {
              // Get school name with fallback for case sensitivity
              const schoolName = getProperty(school, 'schoolName') || school.SchoolName || school.schoolName || 'Unknown School';
              const schoolKey = schoolName || `school-${index}`;
              return (
                <li
                  key={schoolKey}
                  className="text-lg p-2 hover:bg-blue-700 hover:rounded-xl hover:text-white cursor-pointer"
                  onClick={() => handleGrammarSelect(school)}
                >
                  {schoolName}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  if (isTitleDropdown || isYearDropdown || isGenderDropdown) {
    const options = {
      title: ["Mr", "Mrs", "Miss", "Dr"],
      year: ["Year 3", "Year 4", "Year 5"],
      gender: ["M", "F"],
    };
    const currentOptions =
      options[type.replace("Dropdown", "").toLowerCase()] || [];

    return (
      <div className="relative  w-full mb-4">
        <select
          className="appearance-none border bg-white p-4 w-full"
          value={value}
          onChange={onChange}
          name={name}
        >
          <option value="">Select</option>
          {currentOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-600">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    );
  }

  if (isVerification) {
    return (
      <>
        <div className="w-1/5">
          <input
            type={inputType}
            onKeyDown={onKeyDown}
            ref={inputRef}
            maxLength={maxLength}
            className={`p-2 w-full input-two text-white ${
              errorMessage ? "border-red" : "verification-border"
            } focus:outline-none focus:ring-0`}
            value={value}
            onChange={onChange}
          />
          <div className="verification-border">
            <div className="bottom-border"></div>
          </div>
          {errorMessage && (
            <p className="flex justify-start mt-4 text-white text-sm">
              {errorMessage}
            </p>
          )}
        </div>
        <div>
          {errorMessage && (
            <p className="flex justify-start mt-4 text-white text-sm">
              {errorMessage}
            </p>
          )}
        </div>
      </>
    );
  }

  return (
    <div className="w-full mb-4">
      <input
        placeholder={placeholder}
        autoComplete={autoComplete}
        type={inputType}
        name={name}
        className={`p-4 w-full ${
          errorMessage ? "border-red" : "border"
        } focus:outline-none focus:ring-0`}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      />
      {errorMessage && (
        <p className="flex justify-start mt-4 text-red-500 text-sm">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default CustomInput;
